import { AD_VIEW_SECONDS, type AdImpression } from '@accra/shared';
import { Frustum, Matrix4, type PerspectiveCamera } from 'three';
import type { VisibleBoard } from '../world/ads/adBoards';

const MAX_DISTANCE = 170;

/**
 * Counts how long each booked ad is actually on screen. A "view" is a sighting of at least one second;
 * seconds are the total time on screen. Reported to the server at the end of each shift.
 */
export class AdTracker {
  private readonly frustum = new Frustum();
  private readonly matrix = new Matrix4();
  private totals = new Map<string, AdImpression>();
  private streak = new Map<string, number>();

  reset(): void {
    this.totals = new Map();
    this.streak = new Map();
  }

  update(boards: readonly VisibleBoard[], camera: PerspectiveCamera, dt: number): void {
    this.matrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.matrix);
    const seenNow = new Set<string>();
    for (const board of boards) {
      if (!board.campaignId) continue;
      if (board.centre.distanceTo(camera.position) > MAX_DISTANCE || !this.frustum.containsPoint(board.centre)) continue;
      const key = `${board.slotId}|${board.campaignId}`;
      seenNow.add(key);
      const entry = this.totals.get(key) ?? { slotId: board.slotId, campaignId: board.campaignId, views: 0, seconds: 0 };
      entry.seconds += dt;
      const before = this.streak.get(key) ?? 0;
      const after = before + dt;
      if (before < AD_VIEW_SECONDS && after >= AD_VIEW_SECONDS) entry.views += 1;
      this.streak.set(key, after);
      this.totals.set(key, entry);
    }
    // A board that goes off screen starts a new sighting next time.
    for (const key of this.streak.keys()) if (!seenNow.has(key)) this.streak.delete(key);
  }

  /** This shift's impressions, rounded, ready to send. */
  impressions(): AdImpression[] {
    return [...this.totals.values()].filter((i) => i.seconds > 0).map((i) => ({ ...i, seconds: Math.round(i.seconds * 10) / 10 }));
  }
}
